import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, filter, switchMap, take, throwError } from 'rxjs';
import { StorageService } from '../../services/storage/storage.service';
import { AuthService } from '../../services/auth/auth.service';
import { AuthTokenModel } from '../../store/entities/user-management/user/user.model';

const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh-token'];

// pushed to refreshTokenSubject when the refresh fails, so requests queued behind it fail
// too instead of waiting forever for a token that will never come
const REFRESH_FAILED = '';

function addAuthHeader(
  req: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': '*',
    },
  });
}

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
): ReturnType<HttpHandlerFn> {
  if (!auth.isRefreshing) {
    auth.isRefreshing = true;
    // Null blocks any queued requests from proceeding until we have a fresh token
    auth.refreshTokenSubject.next(null);

    return auth.refreshToken().pipe(
      switchMap((tokenData: AuthTokenModel) => {
        // a 200 without a token is a failed refresh too
        if (!tokenData?.access_token) return throwError(() => new Error('Token refresh returned no token'));
        auth.isRefreshing = false;
        auth.refreshTokenSubject.next(tokenData.access_token);
        return next(addAuthHeader(req, tokenData.access_token));
      }),
      catchError((refreshError) => {
        auth.isRefreshing = false;
        auth.refreshTokenSubject.next(REFRESH_FAILED);
        // Refresh itself failed — the session is truly expired, kill it
        auth.collectFailedRequest({
          reason: '',
          status: 401,
          causedBy: 'Token refresh failed',
        });
        return throwError(() => refreshError);
      }),
    );
  }

  // Another request is already refreshing — wait for the new token then retry
  return auth.refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) =>
      token === REFRESH_FAILED
        ? throwError(() => new HttpErrorResponse({ status: 401, url: req.url, statusText: 'Token refresh failed' }))
        : next(addAuthHeader(req, token)),
    ),
  );
}

export const authInterceptor$: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const storage = inject(StorageService);

  // Never attach the access token to auth endpoints — the refresh endpoint uses
  // the refresh token in the body, and sending an expired access token in the
  // Authorization header causes the server to return 401 before even reading it.
  const isAuthEndpoint = AUTH_ENDPOINTS.some((path) => req.url.includes(path));

  const accessToken = storage.get('accessToken');
  if (accessToken && !isAuthEndpoint) {
    req = addAuthHeader(req, accessToken);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isAuthEndpoint) {
        return handle401(req, next, auth);
      }

      // The guest screens (login, forgot password, activation, reset) each
      // render the server's message inline, so a popup would only repeat it.
      if (!accessToken) {
        return throwError(() => error);
      }

      auth.collectFailedRequest({
        reason: error?.error?.reason ?? '',
        status: error.status,
        causedBy: error.message,
      });
      return throwError(() => error);
    }),
  );
};
